import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";

// Palette + type scale lifted 1:1 from AstriaDesign3.html's :root and .hero rules.
const COLORS = {
  amethyst: "#6D4C92",
  darkAmethyst: "#2D1B44",
  roseGold: "#E5B8AD",
  body: "#4A4A4A",
  muted: "#8A7F73",
  cardBorder: "#E8E3DA",
};

const FONTS = {
  head: '"Fraunces", Georgia, serif',
  body: '"Manrope", system-ui, -apple-system, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
};

const STATS = [
  { num: "2.4M+", label: "Readings delivered" },
  { num: "15", label: "Countries served" },
  { num: "340+", label: "Certified astrologers" },
  { num: "4.9/5", label: "Average rating" },
];

interface HeroSectionProps {
  onExploreChart: () => void;
  onCompanionTalk: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  onExploreChart,
  onCompanionTalk,
}) => {

  return (
    <section
      className="w-full grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] items-center gap-8 lg:gap-12"
      style={{
        maxWidth: 900,
        margin: "0 auto",
        padding: "1.5rem 0 2.5rem",
        fontFamily: FONTS.body,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div
          className="flex items-center"
          style={{
            fontFamily: FONTS.mono,
            fontSize: "0.7rem",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: COLORS.amethyst,
            gap: "0.5rem",
            marginBottom: "1.1rem",
            fontWeight: 700,
          }}
        >
          <span
            className="rounded-full flex-shrink-0"
            style={{
              width: 6,
              height: 6,
              backgroundColor: COLORS.amethyst,
              boxShadow: "0 0 8px rgba(109,76,146,0.6)",
            }}
          />
          Trusted in 15 countries &middot; Live celestial data
        </div>

        <h1
          style={{
            fontFamily: FONTS.head,
            fontSize: "clamp(2.1rem, 4.5vw, 3rem)",
            lineHeight: 1.1,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: COLORS.darkAmethyst,
            marginBottom: "1.1rem",
          }}
        >
          Understand what’s ahead,
          <br />
          <span
            style={{
              backgroundImage: `linear-gradient(100deg, ${COLORS.amethyst}, #B76BA3 55%, ${COLORS.roseGold})`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            Prepare for what matters.
          </span>
        </h1>

        <p
          style={{
            fontSize: "1rem",
            lineHeight: 1.7,
            color: COLORS.body,
            maxWidth: "30rem",
            marginBottom: "1.75rem",
          }}
        >
          Astria helps you understand yourself, explore what may be ahead,
          and find clarity for the journey forward — with guidance that feels
          personal, thoughtful, and human.
        </p>

        <div
          className="flex items-center flex-wrap"
          style={{ gap: "0.75rem", marginBottom: "2rem" }}
        >
          <button
            onClick={onExploreChart}
            className="inline-flex items-center transition-transform duration-150"
            style={{
              padding: "0.85rem 1.5rem",
              borderRadius: "0.9rem",
              fontSize: "0.9rem",
              fontWeight: 700,
              color: "#fff",
              background: `linear-gradient(120deg, ${COLORS.amethyst}, #8A5FB8)`,
              gap: "0.5rem",
              boxShadow: "0 10px 24px rgba(109,76,146,0.32)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 14px 30px rgba(109,76,146,0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 10px 24px rgba(109,76,146,0.32)";
            }}
            type="button"
          >
            Explore your chart
            <ArrowRight className="w-[15px] h-[15px]" strokeWidth={2.2} />
          </button>

          <button
            onClick={onCompanionTalk}
            className="inline-flex items-center transition-transform duration-150"
            style={{
              padding: "0.85rem 1.4rem",
              borderRadius: "0.9rem",
              fontSize: "0.9rem",
              fontWeight: 700,
              color: COLORS.darkAmethyst,
              background: "#fff",
              border: `1px solid ${COLORS.cardBorder}`,
              gap: "0.5rem",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = COLORS.amethyst;
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = COLORS.cardBorder;
              e.currentTarget.style.transform = "translateY(0)";
            }}
            type="button"
          >
            <MessageCircle className="w-[15px] h-[15px]" strokeWidth={2} />
            Companion Talk
          </button>
        </div>

        <div className="flex flex-wrap" style={{ gap: "1.5rem" }}>
          {STATS.map((stat) => (
            <div key={stat.label}>
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: "1.3rem",
                  fontWeight: 700,
                  color: COLORS.darkAmethyst,
                }}
              >
                {stat.num}
              </div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: COLORS.muted,
                  marginTop: "0.2rem",
                }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative flex items-center justify-center"
        style={{ height: 340 }}
      >
        <motion.img
          src="/logo_2.png"
          alt="Astria logo"
          className="object-contain"
          style={{ width: 320, height: 320, maxWidth: "100%" }}
          animate={{ scale: [1, 1.035, 1] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />

        <span
          className="absolute whitespace-nowrap"
          style={{
            top: 8,
            left: 0,
            fontFamily: FONTS.mono,
            fontSize: "0.68rem",
            fontWeight: 600,
            color: COLORS.darkAmethyst,
            background: "#fff",
            border: `1px solid ${COLORS.cardBorder}`,
            padding: "0.35rem 0.6rem",
            borderRadius: "0.6rem",
            boxShadow: "0 8px 25px rgba(0,0,0,0.06)",
          }}
        >
          24&deg;17&prime; SCO
        </span>

        <span
          className="absolute whitespace-nowrap"
          style={{
            top: "50%",
            right: -18,
            transform: "translateY(-50%)",
            fontFamily: FONTS.mono,
            fontSize: "0.68rem",
            fontWeight: 600,
            color: COLORS.darkAmethyst,
            background: "#fff",
            border: `1px solid ${COLORS.cardBorder}`,
            padding: "0.35rem 0.6rem",
            borderRadius: "0.6rem",
            boxShadow: "0 8px 25px rgba(0,0,0,0.06)",
          }}
        >
          ASC 08&deg;
        </span>

        <span
          className="absolute whitespace-nowrap"
          style={{
            bottom: 30,
            right: -12,
            fontFamily: FONTS.mono,
            fontSize: "0.68rem",
            fontWeight: 600,
            color: COLORS.darkAmethyst,
            background: "#fff",
            border: `1px solid ${COLORS.cardBorder}`,
            padding: "0.35rem 0.6rem",
            borderRadius: "0.6rem",
            boxShadow: "0 8px 25px rgba(0,0,0,0.06)",
          }}
        >
          &#9737; Trine &#9794;
        </span>
      </motion.div>
    </section>
  );
};

export default HeroSection;
